/**
 * Measure rendered text width, so a chart can truncate a label to the space it actually has.
 *
 * Estimating from character counts does not work here: the labels are a mix of ULIDs, snake_case
 * operation names and dates, and "26 characters" is a very different width for each. Getting it
 * wrong shows up as labels overlapping the bars.
 *
 * One canvas, reused. Measurements are memoised per font because the same handful of labels are
 * measured on every render.
 */
const caches = new Map<string, Map<string, number>>();
let context: CanvasRenderingContext2D | null = null;

function contextFor(font: string): CanvasRenderingContext2D | null {
  if (context === null) {
    context = document.createElement("canvas").getContext("2d");
  }
  if (context !== null) {
    context.font = font;
  }
  return context;
}

/** Width in CSS pixels of `text` rendered in `font` (a CSS shorthand, e.g. "11px sans-serif"). */
export function measureText(text: string, font: string): number {
  let cache = caches.get(font);
  if (cache === undefined) {
    cache = new Map();
    caches.set(font, cache);
  }
  const known = cache.get(text);
  if (known !== undefined) {
    return known;
  }
  // Without a canvas (jsdom, an odd embedding) fall back to a rough estimate rather than
  // throwing: a slightly wrong label beats a chart that will not render.
  const measured = contextFor(font)?.measureText(text).width ?? text.length * 6;
  cache.set(text, measured);
  return measured;
}

/**
 * Shorten `text` with a trailing ellipsis until it fits `limit` pixels.
 *
 * Returns the original when it already fits, so the common case costs one measurement. The caller
 * is expected to keep the full text reachable — a title, a tooltip, a table.
 */
export function fitText(text: string, limit: number, font: string): string {
  if (limit <= 0) {
    return "";
  }
  if (measureText(text, font) <= limit) {
    return text;
  }
  // Binary search the cut rather than stepping a character at a time: labels can be long and this
  // runs for every bar on every render.
  let low = 0;
  let high = text.length;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (measureText(`${text.slice(0, middle)}…`, font) <= limit) {
      low = middle;
    } else {
      high = middle - 1;
    }
  }
  return low === 0 ? "" : `${text.slice(0, low)}…`;
}
